'use client';

import { c as _c } from "react/compiler-runtime";
import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import pluralize from 'pluralize';
import { type ReactNode, useRef, useState } from 'react';
import type { MapFocus, MapMode } from '~/components/map';
import { BywayMapLazy } from '~/components/map/BywayMapLazy';
import mapStyles from '~/components/map/Map.module.css';
import { Spinner } from '~/components/Spinner';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
import type { TripAccommodationStay, TripOverviewStay } from '~/services/engine-ts';
import { AccommodationStaysMapScroller, type AccommodationStaysMapScrollerHandle } from './AccommodationStaysMapScroller';
import sheetStyles from './AccommodationStaysMapSheet.module.css';
import { tripAccommodationStaysToMapData } from './tripAccommodationStaysToMapData';
type AccommodationStaysMapSheetProps = {
  onClose: () => void;
  tripId: string;
};
const parseStayMarkerId = (id: string): number | null => {
  if (!id.startsWith('stay-')) return null;
  const itemId = Number.parseInt(id.slice(5), 10);
  return Number.isFinite(itemId) ? itemId : null;
};
const buildStayTooltip = (stay: TripAccommodationStay | undefined, placeName: string) => {
  if (!stay) return <strong className={mapStyles.popupHeading}>{placeName}</strong>;
  return <>
      <strong className={mapStyles.popupHeading}>{stay.accommodation.title}</strong>
      <div className={mapStyles.popupStayMeta}>{pluralize('night', stay.nights, true)}</div>
      <div className={mapStyles.popupStayMeta}>{stay.placeName}</div>
    </>;
};
const MapSheetShell = t0 => {
  const $ = _c(9);
  const {
    onClose,
    closeLabel,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = clsx(mapStyles.root, mapStyles.rootOverlay, sheetStyles.shell);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = clsx(mapStyles.overlayButton, mapStyles.closeButton);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Xmark decorative={true} size="small" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== closeLabel || $[4] !== onClose) {
    t4 = <button type="button" className={t2} onClick={onClose} aria-label={closeLabel} data-test-id="accommodation-map-close">{t3}</button>;
    $[3] = closeLabel;
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <div className={t1}>{t4}{children}</div>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const AccommodationStaysMapSheet = t0 => {
  const $ = _c(56);
  const {
    onClose,
    tripId
  } = t0;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  const [mode, setMode] = useState("expanded");
  const [focus, setFocus] = useState(null);
  const scrollerRef = useRef(null);
  let t1;
  if ($[0] !== tripId) {
    t1 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!tripId;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t2,
      keepPreviousData: true
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: overview,
    isLoading: isOverviewLoading
  } = api.trip.getOverview.useQuery(t1, t3);
  let t4;
  if ($[4] !== tripId) {
    t4 = {
      tripId
    };
    $[4] = tripId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = !!tripId;
  let t6;
  if ($[6] !== t5) {
    t6 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t5,
      keepPreviousData: true
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    data: accommodation,
    isLoading: isAccommodationLoading
  } = api.trip.getAccommodation.useQuery(t4, t6);
  let activeItemId;
  let handleFocusChange;
  let handleModeChange;
  let handleStaySelect;
  let mapData;
  let t7;
  let t8;
  if ($[8] !== accommodation?.stays || $[9] !== focus || $[10] !== isAccommodationLoading || $[11] !== isOverviewLoading || $[12] !== onClose || $[13] !== overview?.items || $[14] !== t || $[15] !== tAria) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const overviewStays = overview?.items.filter(_temp) ?? [];
      mapData = tripAccommodationStaysToMapData(overviewStays);
      const staysByItemId = new Map((accommodation?.stays ?? []).map(_temp2));
      let t9;
      if ($[23] !== focus) {
        t9 = focus?.kind === "marker" ? parseStayMarkerId(focus.id) : null;
        $[23] = focus;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      activeItemId = t9;
      let t10;
      if ($[25] !== onClose) {
        t10 = next => {
          setMode(next);
          if (next === "compact") {
            onClose();
          }
        };
        $[25] = onClose;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      handleModeChange = t10;
      let t11;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = itemId => {
          setFocus({
            kind: "marker",
            id: `stay-${itemId}`
          });
        };
        $[27] = t11;
      } else {
        t11 = $[27];
      }
      handleStaySelect = t11;
      let t12;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = next_0 => {
          setFocus(next_0);
          if (next_0?.kind === "marker") {
            const itemId_0 = parseStayMarkerId(next_0.id);
            if (itemId_0 !== null) {
              scrollerRef.current?.scrollToStay(itemId_0);
            }
          }
        };
        $[28] = t12;
      } else {
        t12 = $[28];
      }
      handleFocusChange = t12;
      let t13;
      if ($[29] !== tAria) {
        t13 = tAria("close");
        $[29] = tAria;
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      const closeLabel = t13;
      if (isOverviewLoading || isAccommodationLoading) {
        let t14;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <div className={sheetStyles.shellMessage} aria-busy="true" aria-live="polite"><Spinner /></div>;
          $[31] = t14;
        } else {
          t14 = $[31];
        }
        let t15;
        if ($[32] !== closeLabel || $[33] !== onClose) {
          t15 = <MapSheetShell onClose={onClose} closeLabel={closeLabel}>{t14}</MapSheetShell>;
          $[32] = closeLabel;
          $[33] = onClose;
          $[34] = t15;
        } else {
          t15 = $[34];
        }
        t8 = t15;
        break bb0;
      }
      if (mapData.markers.length === 0) {
        let t14;
        if ($[35] !== t) {
          t14 = t("accommodation_map_unavailable");
          $[35] = t;
          $[36] = t14;
        } else {
          t14 = $[36];
        }
        let t15;
        if ($[37] !== t14) {
          t15 = <p className={sheetStyles.shellMessage}>{t14}</p>;
          $[37] = t14;
          $[38] = t15;
        } else {
          t15 = $[38];
        }
        let t16;
        if ($[39] !== closeLabel || $[40] !== onClose || $[41] !== t15) {
          t16 = <MapSheetShell onClose={onClose} closeLabel={closeLabel}>{t15}</MapSheetShell>;
          $[39] = closeLabel;
          $[40] = onClose;
          $[41] = t15;
          $[42] = t16;
        } else {
          t16 = $[42];
        }
        t8 = t16;
        break bb0;
      }
      t7 = mapData.markers.map(marker => {
        const itemId_1 = parseStayMarkerId(marker.id);
        const stay_0 = itemId_1 !== null ? staysByItemId.get(itemId_1) : undefined;
        const overviewStay = itemId_1 !== null ? overviewStays.find(s => s.itemId === itemId_1) : undefined;
        return {
          ...marker,
          tooltip: buildStayTooltip(stay_0, overviewStay?.place.name ?? marker.label ?? "")
        };
      });
    }
    $[8] = accommodation?.stays;
    $[9] = focus;
    $[10] = isAccommodationLoading;
    $[11] = isOverviewLoading;
    $[12] = onClose;
    $[13] = overview?.items;
    $[14] = t;
    $[15] = tAria;
    $[16] = activeItemId;
    $[17] = handleFocusChange;
    $[18] = handleModeChange;
    $[19] = handleStaySelect;
    $[20] = mapData;
    $[21] = t7;
    $[22] = t8;
  } else {
    activeItemId = $[16];
    handleFocusChange = $[17];
    handleModeChange = $[18];
    handleStaySelect = $[19];
    mapData = $[20];
    t7 = $[21];
    t8 = $[22];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  const markersWithTooltips = t7;
  let t9;
  if ($[43] !== accommodation || $[44] !== activeItemId || $[45] !== handleStaySelect) {
    t9 = accommodation?.stays.length ? <AccommodationStaysMapScroller ref={scrollerRef} stays={accommodation.stays} activeItemId={activeItemId} onStaySelect={handleStaySelect} /> : null;
    $[43] = accommodation;
    $[44] = activeItemId;
    $[45] = handleStaySelect;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== focus || $[48] !== handleFocusChange || $[49] !== handleModeChange || $[50] !== mapData.route || $[51] !== mapData.segments || $[52] !== markersWithTooltips || $[53] !== mode || $[54] !== t9) {
    t10 = <BywayMapLazy markers={markersWithTooltips} segments={mapData.segments} route={mapData.route} mode={mode} onModeChange={handleModeChange} focus={focus} onFocusChange={handleFocusChange} showLabels={true} showDetailedViewButton={false} overlayBottomSlot={t9} />;
    $[47] = focus;
    $[48] = handleFocusChange;
    $[49] = handleModeChange;
    $[50] = mapData.route;
    $[51] = mapData.segments;
    $[52] = markersWithTooltips;
    $[53] = mode;
    $[54] = t9;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  return t10;
};
function _temp(item) {
  return item.type === "stay";
}
function _temp2(stay) {
  return [stay.itemId, stay] as const;
}